import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, retry } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Bill, BillResult, Product, ProductRecognized, StoredBill } from '../models/bill';
import { ToolsService } from './tools.service';
import { Contest, Store } from '../models/contest';
import { ContestService } from './contest.service';
import { AuthService } from './auth.service';
import { ActivatedRoute } from '@angular/router';
import { RequestBody } from '../models/request-body';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})

export class BillService {

  azureLink?: string;
  azureKey?: string;
  billServiceKey?: string;
  private uuidRequest: string | undefined
  adminToken: string | null = null;
  recognizedValues: any;
  private readonly modelExecutorJsonSchema = {
    type: 'object',
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    properties: {
      cufe: {
        type: ['string', 'null'],
        description: "Here, write CUFE's the bill. The CUFE is a unique code that identifies the electronic bill in Colombia. You can usually find it at the bottom of the bill and it is an alphanumeric code of 44 characters in length or a QR code that contains it. If you cannot find it, write - as the value",
      },
      date: {
        type: ['string', 'null'],
        description: 'Extract the purchase date from the invoice.\n' +
          '\n' +
          'Return ONLY one date in DD-MM-YYYY format.\n' +
          'Do not return explanations, labels, JSON, additional text, or multiple dates.\n' +
          '\n' +
          'INPUT CONTEXT:\n' +
          '- store_document: {{store_document}}\n' +
          '- reference date: {{current_date}}\n' +
          '\n' +
          'TASK:\n' +
          'Identify the date that represents the actual purchase, transaction, sale, or consumption date.\n' +
          '\n' +
          'Do NOT extract:\n' +
          '- invoice issuance date,\n' +
          '- document printing date,\n' +
          '- billing date,\n' +
          '- due date,\n' +
          '- payment due date,\n' +
          '- authorization date,\n' +
          '- delivery date,\n' +
          '- expiration date,\n' +
          '- POS closing date,\n' +
          '- software version date,\n' +
          '- historical, legal, or copyright dates.\n' +
          '\n' +
          'DATE PRIORITY:\n' +
          '1. Prefer a date explicitly labeled as "Fecha de compra", "Fecha de venta", "Fecha de transacción", "Fecha", "Purchase date", "Transaction date", "Sale date", or an equivalent label.\n' +
          '2. If multiple candidate dates exist, select the one closest to the transaction details, items, total amount, payment method, cashier, terminal, or transaction number.\n' +
          '3. Select only a date that is plausible for the reference date and invoice context.\n' +
          '4. Do not infer or invent a date that is not visibly printed on the invoice.\n' +
          '\n' +
          'FORMAT RULES:\n' +
          '\n' +
          'A. DEFAULT RULE — all stores except store_document "8901074873":\n' +
          '- Interpret numeric dates as DD/MM/YYYY or DD-MM-YYYY.\n' +
          '- Example: 07/09/2025 → 07-09-2025.\n' +
          '- Example: 07-09-2025 → 07-09-2025.\n' +
          '\n' +
          'B. SPECIAL RULE — ONLY when store_document is exactly "8901074873":\n' +
          '- A date with exactly 2 digits / 2 digits / 2 digits MUST be interpreted as YY/MM/DD.\n' +
          '- Never interpret a 2-digit / 2-digit / 2-digit date from this store as DD/MM/YY.\n' +
          '- The first group is always the year.\n' +
          '- The second group is always the month.\n' +
          '- The third group is always the day.\n' +
          '- Convert the two-digit year to a four-digit year using the reference date and a realistic purchase-date range.\n' +
          '\n' +
          'Examples for store_document "8901074873":\n' +
          '- 07/09/07 means 07 September 2007 only if the invoice context clearly indicates year 2007.\n' +
          '- 25/05/21 means 21-05-2025.\n' +
          '- 24/12/31 means 31-12-2024.\n' +
          '- 26/01/15 means 15-01-2026.\n' +
          '- Do NOT interpret 07/09/07 as 07-09-2007 unless the invoice is clearly from the year 2007.\n' +
          '- If the invoice is recent and the date is 07/09/07, treat it as ambiguous and use surrounding invoice evidence to determine the correct recent year. Do not choose 2007 merely because it is a valid calendar date.\n' +
          '\n' +
          'C. SPECIAL RULE — ONLY when store_document is exactly "8901074873":\n' +
          '- If the date is written with a 4-digit first group, interpret it as YYYY/MM/DD.\n' +
          '- Example: 2025/05/21 → 21-05-2025.\n' +
          '- Example: 2024/12/31 → 31-12-2024.\n' +
          '\n' +
          'VALIDATION RULES:\n' +
          '- The final result must be a valid calendar date.\n' +
          '- The purchase date should normally be reasonably close to {{current_date}}, unless the invoice clearly proves it is an older document.\n' +
          '- Reject implausible interpretations when a more recent and contextually supported date exists.\n' +
          '- For store_document "8901074873", never convert a date by assuming the first two digits are the day.\n' +
          '- If no purchase date can be identified with confidence, return an empty value.\n' +
          '\n' +
          'OUTPUT FORMAT:\n' +
          'DD-MM-YYYY',
      },
      hour: {
        type: ['string', 'null'],
        description: 'The hour of the bill in HH24:MM format. If the hour text comes with PM, then is at the afternoon so convert it',
      },
      total: {
        type: ['number', 'null'],
        description: 'Total value of the bill, excluding any discounts or additional charges',
      },
      context: {
        type: ['string', 'null'],
        const: 'documents',
      },
      accuracy: {
        type: ['number', 'null'],
        description: 'Indicates if the recognized response is accurate or not, values are between 0 and 100, where 100 is 100% sure of your answer and 0 not sure.',
      },
      is_valid: {
        type: 'boolean',
        description: 'Indicates whether the image contains a valid bill',
      },
      modified: {
        type: 'boolean',
        description: 'Indicates if the data of the bill has been modified, for example, if the total amount was changed',
      },
      products: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            sku: { type: ['string', 'null'], description: "Product SKU, it is the unique identifier of the product. May contain word 'Código', Cod or similar word" },
            name: { type: ['string', 'null'], description: 'Name of the product' },
            price: { type: ['number', 'null'], description: 'Unit price of the product' },
            discount: { type: ['number', 'null'], description: "Discount of the product, it is a number larger than 100. May contain word 'Desc' or 'Descuento' or 'Desct' or 'Dscto' or similar" },
            quantity: { type: ['number', 'null'], description: 'The quantity of the product. It can be a decimal number but it has to be greater than 0' },
            'discount-percent': { type: ['number', 'null'], description: "Percent (%) of discount of the product, is a number greater than 0 and less than or equal to 100. May contain word 'Desc' or 'Descuento' or 'Desct' or 'Dscto' or similar. It does not need to contain the % character." },
            'measurement-unit': { type: ['string', 'null'], description: "Measurement unit of the product. It can be different for each product. If the product does not have a measurement unit, write - as the value. Sometimes it may be a measurement unit or just a word like 'Unidad', 'Unid', 'und'." },
          },
          required: ['sku', 'name', 'price', 'discount', 'quantity', 'discount-percent', 'measurement-unit'],
          additionalProperties: false,
        },
        description: 'List of products. Group identical items by name and price, summing their quantities. Return only up to 20 products if the list is too long.',
      },
      store_city: { type: ['string', 'null'], description: 'City of the bill issuer (the store). The city should be complete, if the city is missing, set - as the value' },
      store_name: { type: ['string', 'null'], description: "Name of the bill issuer (the store). The name is usually located at the beginning of the document, it is usually the first line of the document. The name may contain the store type (e.g., S.A.S, S.A, Ltda, etc.) and the store name. This field is also known as 'Razon Social'." },
      bill_number: { type: ['string', 'null'], description: 'The unique bill number, delete all spaces' },
      client_city: { type: ['string', 'null'], description: 'City of the client (the person who is going to pay). The city should be complete, if the city is missing, set - as the value' },
      client_email: { type: ['string', 'null'], description: 'Email of the client (the person who is going to pay). If the email is missing, set - as the value. The email should contain the @ character. If the email is not clearly identifiable, return -' },
      client_phone: { type: ['string', 'null'], description: 'Phone number of the client (the person who is going to pay). If the phone number is missing, set - as the value. The phone number should only contain digits, and it may contain the prefix +57 or 57. If the phone number is not clearly identifiable, return -' },
      store_address: { type: ['string', 'null'], description: "Extract only the complete street address of the invoice issuer (the store) from this invoice. The address is usually located at the beginning or at the end of the document, close to the issuer's tax ID, store name, or contact details. If there are multiple addresses, extract the second one, which is typically found at the bottom of the invoice near the contact details. The street address usually starts with standard abbreviations such as CL, Calle, Cra, Carrera, Av, Avenida, Trans, Tv, Dg, Diagonal, etc., followed by numbers and may contain symbols like '#' or '-'. Only include the street type abbreviation (e.g., CL, Calle, Cra), followed by the street and property numbers. If there is additional adjacent data such as phone numbers, city names, neighborhood names, ZIP codes, store names, emails, or website URLs, exclude all of them-return strictly the address portion only. Ensure there are no trailing or leading spaces. If the address is not clearly identifiable or missing, return '-'" },
      client_address: { type: ['string', 'null'], description: "Extract only the complete street address of the client or the person who is going to pay from this invoice. The address is usually located at the beginning or at the end of the document, close to the issuer's tax ID, store name, or contact details. If there are multiple addresses, extract the second one, which is typically found at the bottom of the invoice near the contact details. The street address usually starts with standard abbreviations such as CL, Calle, Cra, Carrera, Av, Avenida, Trans, Tv, Dg, Diagonal, etc., followed by numbers and may contain symbols like '#' or '-'. Only include the street type abbreviation (e.g., CL, Calle, Cra), followed by the street and property numbers. If there is additional adjacent data such as phone numbers, city names, neighborhood names, ZIP codes, store names, emails, or website URLs, exclude all of them-return strictly the address portion only. Ensure there are no trailing or leading spaces. If the address is not clearly identifiable or missing, return ' - ' " },
      payment_method: { type: ['string', 'null'], description: 'The method of payment used in the bill, it could be card, cash or credit, or other' },
      store_document: { type: ['string', 'null'], description: 'Extract the document number (tax ID / NIT) of the bill issuer, meaning the store that issued the invoice.  Return digits only, without spaces, punctuation, labels, or additional text.  The store document number is usually located near the beginning of the document, close to the store name, legal business name, "NIT", "Tax ID", "Identification", or similar issuer information.  Do not extract the customer\'s identification number, payment reference, authorization number, invoice number, transaction number, terminal number, or any other document number.' },
      client_document: { type: ['string', 'null'], description: 'Document number of the client. It should only contain digits' },
      client_document_type: { type: ['string', 'null'], description: 'Document type of the client. May be NIT, CC (cedula ciudadanía) or CE (Cedula extranjería). If the type is not in the options, set it literally as it is in the document. If the document type is missing, set - as the value' },
    },
    required: ['cufe', 'date', 'hour', 'total', 'context', 'accuracy', 'is_valid', 'modified', 'products', 'store_city', 'store_name', 'bill_number', 'client_city', 'client_email', 'client_phone', 'store_address', 'client_address', 'payment_method', 'store_document', 'client_document', 'client_document_type'],
    additionalProperties: false,
  };

  constructor(public httpClient: HttpClient, private toolsService: ToolsService, private route: ActivatedRoute, private contestService: ContestService, private authSvc: AuthService, private configService: ConfigService) { }

  async getAzureKey(): Promise<string> {
    if (this.azureKey) return this.azureKey;
    return this.toolsService.convertObservableToPromise<any>(this.getSystemParam('AZURE_KEY')).then((data) => {
      this.azureKey = data.value;
      return this.azureKey ? this.azureKey : '';
    });
  }

  async getAzureLink(): Promise<string> {
    if (this.azureLink) return this.azureLink;
    return this.toolsService.convertObservableToPromise<any>(this.getSystemParam('AZURE_LINK')).then((data) => {
      this.azureLink = data.value;
      return this.azureLink ? this.azureLink : '';
    });
  }

  async getBillServiceKey(): Promise<string> {
    if (this.billServiceKey) return this.billServiceKey;
    return this.toolsService.convertObservableToPromise<any>(this.getSystemParam('BILLS_SERVICE_KEY')).then((data) => {
      this.billServiceKey = data.value;
      return this.billServiceKey ? this.billServiceKey : '';
    });
  }

  getSystemParam(paramKey: string) {
    let url = `${environment.serverUrl}/configuration/param/${paramKey}`;
    let params: any = {};
    return this.httpClient.get<{ key: string, value: string }>(url, { params });
  }

  /**
   * Uploads a bill to the server
   * @deprecated Use analizeBillsV2 instead
   * @returns Observable<any>
   */
  // async analizeBills(contest: Contest, images: Array<string>): Promise<Bill> {
  //   // Present progress loading
  //   await this.toolsService.presentProgressLoading(0, "Cargando imagenes...");
  //   // Upload images to validation service on Azure OCR
  //   let promises: Array<Promise<string>> = [];
  //   images.forEach(async (image) => {
  //     promises.push(this.analizeBill(image));
  //     await this.toolsService.updateProgressLoading((promises.length * 15) / images.length, `Subiendo imagen ${promises.length} de ${images.length}...`);
  //   });
  //   // Update progress loading
  //   await this.toolsService.updateProgressLoading(20, "Reconociendo factura...");
  //   // If any error happens, dismiss progress loading and show error
  //   let promisesData: Array<string> = [];
  //   try {
  //     promisesData = await Promise.all(promises);
  //   } catch (error) {
  //     await this.toolsService.dismissProgressLoading();
  //     await this.toolsService.presentErrorToast("No se pudo analizar la factura, intenta de nuevo más tarde");
  //     return Promise.reject("AZURE_OCR_ERROR");
  //   }
  //   // Update progress loading
  //   await this.toolsService.updateProgressLoading(60, "Analizando factura...");
  //   let completeText = promisesData.join();
  //   // console.debug('BillService.analizeBills()', promisesData, completeText);
  //   // Get bill information using bills service on lambda
  //   let billInformation: BillResult;
  //   try {
  //     billInformation = await this.getBillInformation(completeText);
  //   } catch (error) {
  //     await this.toolsService.dismissProgressLoading();
  //     return Promise.reject("BILL_SERVICE_ERROR");
  //   }
  //   await this.toolsService.updateProgressLoading(80, "Confirmando valores reconocidos...");

  //   // Store the bill request
  //   // let billRequest: any = null;
  //   // try {
  //   //   billRequest = await this.toolsService.convertObservableToPromise(
  //   //     this.storeBillRequest(contest, billInformation, completeText)
  //   //   );
  //   // } catch (error) {
  //   //   console.warn('BillService.validateRecognizedBill() Could not store bill request', error);
  //   // }

  //   let billData = await this.validateRecognizedBill(contest, billInformation, stores);
  //   billData.azureResponse = completeText;
  //   await this.toolsService.dismissProgressLoading();
  //   console.log('el bill data', billData)
  //   return billData;
  // }

  /**
   * Uploads a bill to the server
   * @returns Observable<any>
   */
  async getBillInformation(recognizedText: string): Promise<BillResult> {
    let billServiceKey = await this.getBillServiceKey();
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `token ${billServiceKey}`
    };
    return this.toolsService.convertObservableToPromise<BillResult>(
      this.httpClient.post<BillResult>(environment.billsServiceUrl, { text: recognizedText }, { headers }).pipe(retry(2))
    );
  }

  sleep(milliseconds: number) {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
  }

  /**
   * This method converts a base64 string to a Blob
   * @returns Blob
   */
  private makeblobImage(dataURL: string) {
    const BASE64_MARKER = ';base64,';
    if (dataURL.indexOf(BASE64_MARKER) == -1) {
      const parts = dataURL.split(',');
      const contentType = parts[0].split(':')[1];
      const raw = decodeURIComponent(parts[1]);
      return new Blob([raw], { type: contentType });
    }
    const parts = dataURL.split(BASE64_MARKER);
    const contentType = parts[0].split(':')[1];
    const raw = window.atob(parts[1]);
    const rawLength = raw.length;
    const uInt8Array = new Uint8Array(rawLength);
    for (let i = 0; i < rawLength; ++i)  uInt8Array[i] = raw.charCodeAt(i);
    return new Blob([uInt8Array], { type: contentType });
  };

  private getMimeTypeFromBase64(dataURL: string): string {
    const match = dataURL.match(/^data:([^;]+);base64,/);
    return match?.[1] || 'image/jpeg';
  }

  private getFileExtensionFromMimeType(mimeType: string): string {
    const parts = mimeType.split('/');
    if (parts.length < 2) return 'jpg';
    if (parts[1] === 'jpeg') return 'jpg';
    return parts[1];
  }

  private buildModelExecutorHeaders() {
    return new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Api-Key ${environment.modelExecutorApiKey}`,
    });
  }

  private buildS3UploadHeadersRecord(uploadHeaders: Record<string, string>) {
    const headers: Record<string, string> = {};
    const allowedHeaderKeys = ['content-type', 'x-amz-acl'];
    Object.entries(uploadHeaders || {}).forEach(([key, value]) => {
      if (allowedHeaderKeys.includes(key.toLowerCase())) {
        headers[key] = value;
      }
    });
    return headers;
  }

  private getModelExecutorRequestUrl(path: string = ''): string {
    return `${environment.modelExecutorUrl}${path}`;
  }


  /**
   * Analizes a bill image using Azure OCR
   * @param image Base64 image
   * @deprecated
   * @returns
   */
  private async analizeBill(image: string): Promise<string> {
    const body = this.makeblobImage(image);

    let azureUrl = await this.getAzureLink();
    let azureKey = await this.getAzureKey();

    let url = `${azureUrl}vision/v2.0/recognizeText?mode=Printed`;
    const headers = {
      'Content-Type': 'application/octet-stream',
      'Ocp-Apim-Subscription-Key': azureKey
    };
    // Get the response from Azure
    let data = await this.toolsService.convertObservableToPromise<any>(this.httpClient.post<any>(url, body, { headers, observe: 'response' }).pipe(retry(2)));
    console.debug('BillService.analizeBill()', data);
    // Get the operation location from response headers
    const operationLocation = data.headers.get('operation-location');
    // If no operation location, return error
    if (!operationLocation) return Promise.reject('Azure error');
    console.debug('BillService.analizeBill()', data, operationLocation);
    // Get the analized text from Azure
    return await this.getAnalizedText(operationLocation);
  }

  /**
   * Retrieves the analized text from Azure
   * @param operationUrl
   * @param numberOfTries
   * @returns
   */
  async getAnalizedText(operationUrl: string, numberOfTries: number = 0): Promise<string> {
    if (numberOfTries > 15) return Promise.reject('Azure did not respond');
    else if (numberOfTries > 0) await this.sleep(3000);
    // Get the azure key
    let azureKey = await this.getAzureKey();
    // Set the headers
    const headers = {
      'Ocp-Apim-Subscription-Key': azureKey
    };
    try {
      // Get the response from Azure
      let data = await this.toolsService.convertObservableToPromise<any>(
        this.httpClient.get<any>(operationUrl, { headers }).pipe(retry(2))
      );
      console.debug('BillService.getAnalizedText()', data);
      // If the status is Succeeded, return the text
      if (data.status == "Succeeded") return this.fixAnalizedText(data);
      // If the status is not Succeeded, try again
      return await this.getAnalizedText(operationUrl, numberOfTries + 1);

    } catch (error) {
      console.error('BillService.getAnalizedText()', error);
      return Promise.reject(error);
    }
  }

  /**
   * Concatenates the analized text from azure response
   * @param data
   * @returns
   */
  fixAnalizedText(data: any): string {
    let text = '';
    data.recognitionResult.lines.forEach((line: any) => {
      text += '\n' + line.text;
    });
    return text;
  }

  /**
   * Validate recognized bill data with the contest data
   * @param billData
   * @returns
   */

  async validateRecognizedBill(
    contest: Contest,
    billData: BillResult,
    stores: any
  ): Promise<Bill> {
    console.debug('BillService.validateRecognizedBill()', contest, billData);
    // Store the errors as boolean, we use this to show the error in the alert

    let billErrors: any = {
      Fecha: false,
      Hora: false,
      'Total de compra': false,
      'Número de factura': false,
      Tienda: false,
    };

    // Create the bill object
    let billResult: Bill = {
      billNumber: null,
      store: null,
      store_name: billData.store_name?.trim() || null,
      date: null,
      hour: null,
      total: null,
      products: [],
      // billRequest: billRequestId
    };

    // Validate the hour
    if (billData.hour && billData.hour.trim() != '')
      billResult.hour = billData.hour;
    else billErrors['Hora'] = true;
    // Validate the total, check if is not null and is a number
    if (billData.total_bill != null && !Number.isNaN(billData.total_bill))
      billResult.total = Number.parseFloat(billData.total_bill);
    else billErrors['Total de compra'] = true;
    // Validate the bill number
    if (billData.bill_number && billData.bill_number.trim() != '') {
      billResult.billNumber = billData.bill_number.trim().slice(0, 64);
    } else billErrors['Número de factura'] = true;
    // Validate the products
    if (billData.products && billData.products.length > 0) {
      billData.products.forEach((product) => {
        // Create the product object
        let productToAdd: Product = {
          code: 'COD',
          name: 'Producto',
          quantity: 1,
          price: 0,
        };
        // Validate the code
        if (product.code && product.code.trim() != '')
          productToAdd.code = product.code.trim();
        // Validate the name
        if (product.name && product.name.trim() != '')
          productToAdd.name = product.name.trim();
        // Validate the quantity if is a number or a string that can be converted to a number
        if (
          product.quant &&
          typeof product.quant == 'string' &&
          product.quant.trim() != ''
        )
          productToAdd.quantity = Number.parseInt(product.quant);
        else if (product.quant && typeof product.quant == 'number')
          productToAdd.quantity = product.quant;
        // Validate the price
        if (
          product.price &&
          typeof product.price == 'string' &&
          product.price.trim() != ''
        )
          productToAdd.price = Number.parseFloat(product.price);
        else if (product.price && typeof product.price == 'number')
          productToAdd.price = product.price;
        // Store the product in the bill
        billResult.products.push(productToAdd);
      });
    }
    // Validate the date
    if (billData.date && billData.date.trim() !== '') {
      const recognizedDate = billData.date.trim().split('T')[0];
      billResult.date = recognizedDate.replace(/^(\d{2})-(\d{2})-(\d{4})$/, '$3-$2-$1');
    } else {
      billErrors['Fecha'] = true;
    }

    // Validate the store
    if (billData.NIT && billData.NIT.trim() !== '') {
      let recognizedNit = String(billData.NIT).replace(/\D/g, '');

      billResult.NIT = recognizedNit;

    } else {
      billResult.store = null;
    }

    // if (!billResult.NIT) billErrors['Tienda'] = true;


    this.toolsService.showBillErrorAlert(billErrors);

    return billResult;
  }


  /**
   * Uploads a bill requests to the server
   * @returns Observable<any>
   */
  //  storeBillRequest(contest: Contest, billRequest: BillResult, azureText: string) {
  //   const body = {
  //     contest: contest.id,
  //     recognized_values: billRequest,
  //     azure_response: azureText
  //   };

  //   let url = `${environment.serverUrl}/mall/bill/request/`;
  //   return this.httpClient.post(url, body);
  // }

  //modificado
  // transformImages(images: Array<string>): Array<{ image: string }> {
  //   return images.map(image => {
  //     let urlFromData = image.match(/data:(.*)/)?.[0] || '';
  //     return { image: urlFromData };
  //   });
  // }

  transformImages(images: Array<string> | null): Array<{ image: string }> {
    if (!images || images.length === 0) {
      return [];
    }

    return images.map(image => {
      if (typeof image === 'string' && image.includes('data:')) {
        let urlFromData = image.match(/data:(.*)/)?.[0] || '';
        return { image: urlFromData };
      }
      return { image: '' };
    });
  }


  /**
   *  This function send the bill to server after validation
   * @returns Observable<any>
   */
  storeBill(bill: Bill, contestId: string | undefined, images?: Array<string> | undefined, modified?: boolean, notReaded?: any): Observable<StoredBill> {
    // Bill number demo prefix
    let date = new Date();
    // let billNumberPrefix = 'DEMO-' + date.getFullYear() + date.getMonth() + date.getDate() + date.getHours() + date.getMinutes() + date.getSeconds();
    let billNumberPrefix = '';
    if (this.isDemo) {
      billNumberPrefix = 'DEMO-' + date.getFullYear() + date.getMonth() + date.getDate() + date.getHours() + date.getMinutes() + date.getSeconds() + ' - ';
    }
    let headers = this.authSvc.getAuthHeaders();
    let store = bill.store?.id
    let imagesArray: any = images;
    //modificado
    // let transformedImages = this.transformImages(imagesArray);
    let transformedImages = this.transformImages(imagesArray || []);
    let totalAsFloat = parseFloat(String(bill.total) || '0');
    //modificado
    // if (this.recognizedValues.hasOwnProperty('products')) {
    //   delete this.recognizedValues['products'];
    // }
    if (this.recognizedValues && this.recognizedValues.hasOwnProperty('products')) {
      delete this.recognizedValues['products'];
    }
    const body: RequestBody = {
      "request_uuid": this.uuidRequest,
      "campaign": contestId,
      //modificado
      // "recognized_values": this.recognizedValues,
      "recognized_values": this.recognizedValues || { "manual": true },
      "recognized_text": "",
      "store": store,
      "bill_number": billNumberPrefix + bill.billNumber,
      "total": totalAsFloat,
      "date": new Date(bill.date ? bill.date : '').toISOString().split("T")[0],
      "modified_fields": bill.modified_fields,
      "photos": transformedImages,
      "products": bill.products,
      "hour": bill.hour
    };
    if (this.adminToken) {
      body["created_by_token"] = this.adminToken;
    }
    let url = `${environment.serverUrl}/thoth/bills/create`;
    return this.httpClient.post<StoredBill>(url, body, headers);
  }

  async getBillData(uuidRequest: string) {
    console.debug('BillService.getBillData()', uuidRequest);
    this.uuidRequest = uuidRequest;
    let url = this.getModelExecutorRequestUrl(`/${uuidRequest}/`);
    return this.toolsService.convertObservableToPromise<any>(
      this.httpClient.get<any>(url).pipe(retry(2)
      ));
  }


  /**
   * Upload an image to process with Model Executor.
   * @param image
   */
  async uploadImageToProcess(image: string): Promise<string> {
    const mimeType = this.getMimeTypeFromBase64(image);
    const fileExtension = this.getFileExtensionFromMimeType(mimeType);
    const sourceImageBlob = this.makeblobImage(image);
    const configuredJsonSchema = this.configService.mall?.json_upload;
    const jsonSchema = configuredJsonSchema && Object.keys(configuredJsonSchema).length > 0
      ? configuredJsonSchema
      : this.modelExecutorJsonSchema;
    const body = {
      file_type: mimeType === 'image/jpeg' ? 'application/jpeg' : mimeType,
      file_name: `temp.${fileExtension}`,
      json_schema: jsonSchema,
      metadata: {
        external_id: `${Date.now()}`,
      },
    };

    const uploadRequestResponse = await this.toolsService.convertObservableToPromise<any>(
      this.httpClient
        .post<any>(this.getModelExecutorRequestUrl('/upload/'), body, {
          headers: this.buildModelExecutorHeaders(),
        })
        .pipe(retry(2))
    );

    const uploadContentType =
      uploadRequestResponse.upload_headers?.['Content-Type'] || mimeType;
    const imageBlob = new Blob([sourceImageBlob], {
      type: uploadContentType,
    });

    const uploadResponse = await fetch(uploadRequestResponse.upload_url, {
      method: uploadRequestResponse.upload_method || 'PUT',
      headers: this.buildS3UploadHeadersRecord(
        uploadRequestResponse.upload_headers || {}
      ),
      body: imageBlob,
    });

    if (!uploadResponse.ok) {
      const uploadErrorText = await uploadResponse.text().catch(() => '');
      console.error('ModelExecutor.s3UploadFailed', {
        status: uploadResponse.status,
        statusText: uploadResponse.statusText,
        body: uploadErrorText,
      });
      throw new Error(`S3_UPLOAD_FAILED_${uploadResponse.status}`);
    }

    await this.toolsService.convertObservableToPromise<any>(
      this.httpClient
        .post<any>(
          this.getModelExecutorRequestUrl(`/${uploadRequestResponse.request_id}/uploaded/`),
          {
            object_key: uploadRequestResponse.storage_key,
          },
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
            }),
          }
        )
        .pipe(retry(2))
    );

    return uploadRequestResponse.request_id;
  }


  /**
   * Analizes a bill image using Model Executor.
   * @param contest
   * @param image
   * @returns
   */
  async analizeBillV2(contest: Contest, image: string): Promise<Bill> {
    const loadingPhrases = {
      0: 'Estamos procesando tu factura...',
      20: 'Subiendo tu factura, esto no tomará mucho...',
      40: 'Estamos analizando los detalles...',
      60: 'Casi listo, verificando información...',
      80: 'Solo un momento más, estamos terminando...',
      100: 'Confirmando los datos reconocidos...',
      10000: 'Estamos tardando un poco más de lo esperado...',
    } as any;

    // Present progress loading
    this.toolsService.startLoading(loadingPhrases[0]);
    let timeoutReached = false;

    const timeout = setTimeout(async () => {
      timeoutReached = true;
      this.toolsService.updateLoadingPhrase(
        loadingPhrases[10000],
        'assets/img/loading2.gif'
      );
      await new Promise((resolve) => setTimeout(resolve, 3000));
      timeoutReached = false;
    }, 10000);

    try {
      // Upload image to validation service
      this.toolsService.updateLoadingPhrase(loadingPhrases[20]);
      const uuidRequest = await this.uploadImageToProcess(image);
      let iterations = 0;
      let validationErrorCount = 0;
      let recognizedBillData: any;
      let currentIterationText = loadingPhrases[40];

      while (validationErrorCount < 4) {
        try {
          if (iterations > 20) throw new Error('TIMEOUT_ERROR');
          await this.sleep(2000);

          // Update progress loading until 80%
          let currentProgress = 26 + iterations * 6;
          if (currentProgress > 80) currentProgress = 80;
          if (loadingPhrases[iterations * 2])
            currentIterationText = loadingPhrases[iterations * 2];

          if (!timeoutReached) {
            this.toolsService.updateLoadingPhrase(currentIterationText);
          }

          const billDataRequest: any = await this.getBillData(uuidRequest);
          if (billDataRequest.status === 'FAILED') {
            throw new Error(billDataRequest.error || 'MODEL_EXECUTOR_FAILED');
          }

          if (billDataRequest.status !== 'COMPLETED') {
            iterations++;
            continue;
          }

          recognizedBillData = billDataRequest.result?.result || {};
          this.recognizedValues = recognizedBillData;
          break;
        } catch (error) {
          console.error('BillService.analizeBillV2()', error);
          validationErrorCount++;
        }
      }

      if (!recognizedBillData) {
        throw new Error('MODEL_EXECUTOR_EMPTY_RESULT');
      }

      this.toolsService.updateLoadingPhrase(loadingPhrases[100]);

      // let billRequest: any;
      // try {
      //   billRequest = await this.toolsService.convertObservableToPromise(
      //     this.storeBillRequest(contest, recognizedBillData, "MALLGO MODEL")
      //   );
      // } catch (error) {
      //   console.warn('BillService.validateRecognizedBill() Could not store bill request', error);
      // }

      let stores: any;
      // try {
      //   stores = await this.toolsService.convertObservableToPromise(
      //     this.contestService.getStoresByContest(contest.id)
      //   );
      // } catch (error) {
      //   console.warn(
      //     'BillService.validateRecognizedBill() Could not store bill request',
      //     error
      //   );
      // }

      const recognizedProducts: Array<ProductRecognized> =
        (recognizedBillData.products || []).map((p: any) => ({
          code: p.sku,
          name: p.name,
          quant: p.quantity,
          price: p.price,
        }));

      const billInformation: BillResult = {
        bill_number: recognizedBillData.bill_number,
        total_bill: recognizedBillData.total,
        NIT: recognizedBillData.store_document,
        store_name: recognizedBillData.store_name,
        prefix: '',
        date: recognizedBillData.date,
        hour: recognizedBillData.hour,
        products: recognizedProducts,
      };

      // const billData = await this.validateRecognizedBill(contest, billInformation, billRequest ? billRequest.id : null);
      const billData = await this.validateRecognizedBill(
        contest,
        billInformation,
        stores
      );
      billData.azureResponse = 'MALLGO MODEL';
      return billData;
    } catch (error) {
      console.debug('BillService.analizeBillV2()', error);
      return Promise.reject('BILL_SERVICE_ERROR');
    } finally {
      clearTimeout(timeout);
      this.toolsService.stopLoading();
    }
  }

  /**
   * This method validates if the domain is localhost or contains staging, if so, it will return true
   */
  get isDemo() {
    // if (location.hostname === 'localhost' || location.hostname.includes('staging')) {
    if (location.hostname === 'localhost') {
      return true;
    }
    return false;
  }

  setTicketPrinted(ticketId: any): Observable<any> {
    let url = `${environment.serverUrl}/thoth/campaigns/ticket/${ticketId}`;
    let body = { printed: true };
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.adminToken
      })
    }
    return this.httpClient.put<any>(url, body, httpOptions);
  }

  listGeneratedTickets(contestId: any): Observable<any> {
    let url = `${environment.serverUrl}/thoth/campaigns/tickets/${contestId}`;
    let headers = this.authSvc.getAuthHeaders();
    return this.httpClient.get<any>(url, headers);
  }

  getProfileInfoById(id: any) {
    let url = `${environment.serverUrl}/horus/crm/profile/${id}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.adminToken
      })
    }
    return this.httpClient.get<any>(url, httpOptions);
  }
}
